import { useEffect, useState } from 'react';
import { EmptyState } from '../EmptyState';
import { Button, Input } from '../ui';
import {
  fetchEmailLog,
  type EmailLogRow,
  type CampaignSummary,
} from '../../lib/members-admin-queries';

/**
 * Admin "Email Log" panel. Reads our own email_log (written by the bulk-email
 * endpoint, kept current by the Resend webhook) so the team can see what went
 * out and how it landed - sent / delivered / bounced / complained - without
 * leaving the app. Each row links to the message in Resend for full detail.
 */

const STATUS_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  sent:              { bg: '#EFEDE7', fg: '#6F6A63', label: 'Sent' },
  delivered:         { bg: '#E5F0E8', fg: '#2F6B43', label: 'Delivered' },
  delivery_delayed:  { bg: '#FBF1DD', fg: '#8A6A1F', label: 'Delayed' },
  bounced:           { bg: '#F6E2DF', fg: '#A33A2A', label: 'Bounced' },
  complained:        { bg: '#F6E2DF', fg: '#A33A2A', label: 'Complaint' },
  failed:            { bg: '#F6E2DF', fg: '#A33A2A', label: 'Failed' },
};

function StatusChip({ status }: { status: string }) {
  const s = STATUS_STYLE[status] ?? { bg: '#EFEDE7', fg: '#6F6A63', label: status };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 999,
      background: s.bg,
      color: s.fg,
      fontSize: '0.78em',
      fontFamily: 'Helvetica,Arial,sans-serif',
      letterSpacing: '0.02em',
    }}>
      {s.label}
    </span>
  );
}

function fmt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString();
}

export function EmailLog({ active }: { active: boolean }) {
  const [logs, setLogs] = useState<EmailLogRow[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEmailLog({ q: q.trim() || undefined, status: statusFilter || undefined });
      setLogs(data.logs);
      setCampaigns(data.campaigns);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load the email log.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (active && !loaded) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <>
      <div className="admin__panel-head">
        <h2>Email Log</h2>
        <span className="admin__count">{logs.length} recent</span>
      </div>

      {error && (
        <div className="admin__error">
          <p className="kicker">Problem</p>
          <p>{error}</p>
        </div>
      )}

      {/* Campaign summaries */}
      {campaigns.length > 0 && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <span className="onb__label">Recent campaigns</span>
          <div style={{ display: 'grid', gap: 8, marginTop: 6 }}>
            {campaigns.map((c) => (
              <div key={c.campaign_id} style={{
                display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
                padding: '8px 12px', border: '1px solid var(--smoke)', borderRadius: 'var(--radius-sm)',
              }}>
                <strong style={{ marginRight: 'auto' }}>{c.subject || '(no subject)'}</strong>
                <span style={{ color: 'var(--text-soft)', fontSize: '0.85em' }}>{fmt(c.created_at)}</span>
                <span style={{ fontSize: '0.85em' }}>{c.total} sent</span>
                {Object.entries(c.counts).map(([st, n]) => (
                  <span key={st}><StatusChip status={st} /> {n}</span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="admin__add">
        <div className="admin__add-row">
          <label style={{ flex: 1 }}>
            <span>Search</span>
            <Input
              type="search"
              placeholder="Recipient or subject"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void refresh(); }}
            />
          </label>
          <label>
            <span>Status</span>
            <select
              className="admin__select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="delivery_delayed">Delayed</option>
              <option value="bounced">Bounced</option>
              <option value="complained">Complaint</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <div className="admin__add-actions">
            <Button type="button" variant="ghost" size="md" onClick={refresh} disabled={loading}>
              {loading ? '…' : 'Refresh'}
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      {logs.length === 0 ? (
        <EmptyState
          tone="compact"
          align="start"
          eyebrow="Nothing yet"
          headline={loaded ? 'No emails logged.' : 'Loading…'}
          body="Sends from the Members tab appear here with their delivery status."
        />
      ) : (
        <div className="admin__table-wrap">
          <table className="admin__table">
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Sent</th>
                <th aria-label="Link"></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((r) => (
                <tr key={r.id}>
                  <td className="admin__cell-email"><code title={r.recipient_email}>{r.recipient_email}</code></td>
                  <td>{r.subject || ' - '}{r.kind === 'test' && <span className="pill" style={{ marginLeft: 6 }}>test</span>}</td>
                  <td title={r.detail ?? undefined}><StatusChip status={r.status} /></td>
                  <td className="admin__cell-time" title={fmt(r.created_at)}>{fmt(r.created_at)}</td>
                  <td>
                    {r.resend_id ? (
                      <a
                        href={`https://resend.com/emails/${r.resend_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn--ghost btn--sm"
                      >
                        View in Resend
                      </a>
                    ) : (
                      <span style={{ color: 'var(--text-soft)' }}> - </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
